import type { FacetOptionLocal } from './Filter.types';

export const STYLE_FACET = 'style';
export const COLOR_FACET = 'color';
export const FREE_FACET = 'free';
export const FEATURE_FACET = 'feature';

/**
 * The facets the gallery PRESENTS, in pill order (Jeff, 2026-10-08 huddle: "style, color,
 * feature"; Free pulled out as its own pill between Colour and Feature, Arun 2026-10-15).
 * `style` and `color` are backed one-to-one by the matching composite-code group; `free` and
 * `feature` collect positive options of several underlying groups (see `FIXED_FACET_OPTIONS`).
 * Underlying codes stay as they are so `?pf=` deep links from the expanded nav (and old shared
 * URLs) keep working.
 */
export const FACET_ORDER = [STYLE_FACET, COLOR_FACET, FREE_FACET, FEATURE_FACET] as const;

/** Orientation options are tagged per template (`utils.ts`), not declared locally. */
type OrientationOption = 'orientation:portrait' | 'orientation:landscape' | 'orientation:square';

const FREE_OPTION: FacetOptionLocal = 'price:basic';

/**
 * Feature options in display order — also the order the idle pill cycles through to
 * advertise them. Only these render; their "without" / "classic" / "premium" / "paper"
 * counterparts remain filterable via URL but are not offered (positive features only).
 * Free is absent: it is its own pill.
 */
export const FEATURE_OPTIONS: ReadonlyArray<FacetOptionLocal | OrientationOption> = [
  'format:digital',
  'photo:with',
  'foil:with',
  'size:postcard',
  'product:magnet',
  'orientation:portrait',
  'orientation:landscape',
  'orientation:square',
];

export const FIXED_FACET_OPTIONS: Readonly<Record<string, ReadonlyArray<string>>> = {
  [FREE_FACET]: [FREE_OPTION],
  [FEATURE_FACET]: FEATURE_OPTIONS,
};

/** A facet with a single fixed option renders as a toggle pill, not a rail trigger. */
export const isToggleFacet = (groupCode: string) => FIXED_FACET_OPTIONS[groupCode]?.length === 1;

/**
 * A facet whose options are a fixed list drawn from several underlying groups (Free, Feature),
 * as opposed to one backed one-to-one by its own tag group (Style, Colour).
 */
export const isComposedFacet = (groupCode: string) => !!FIXED_FACET_OPTIONS[groupCode];
